import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const KEYS = ["days", "branch_id", "camera_id", "detection_type", "status", "severity", "date_from", "date_to", "zone_name", "critical_open", "alert_id"];
const DAYS = new Set(["7", "30", "90"]);
const STATUSES = new Set(["nueva", "en_revision", "confirmada", "falsa_alarma", "resuelta"]);
const SEVERITIES = new Set(["LOW", "MEDIUM", "HIGH", "CRITICAL", "UNKNOWN"]);
const API_KEYS = {
  executive: ["days", "branch_id", "camera_id", "detection_type", "status", "severity"],
  operations: ["days", "branch_id"],
  alerts: ["branch_id", "camera_id", "detection_type", "status", "severity", "date_from", "date_to", "zone_name", "critical_open"],
  detectionAnalytics: ["branch_id", "camera_id", "detection_type", "status", "severity", "date_from", "date_to"],
  transversalAnalytics: ["branch_id", "camera_id", "detection_type", "status", "severity", "date_from", "date_to", "zone_name"],
};

const localDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const validDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};
const validId = (value) => /^[1-9]\d*$/.test(value) && Number.isSafeInteger(Number(value));

export const parseSharedFilters = (input, profile = "") => {
  const params = input instanceof URLSearchParams ? input : new URLSearchParams(input || "");
  const result = Object.fromEntries(KEYS.map((key) => [key, ""]));
  for (const key of KEYS) {
    const value = String(params.get(key) || "").trim();
    if (!value) continue;
    if (key === "days" && DAYS.has(value)) result[key] = value;
    else if (["branch_id", "camera_id", "alert_id"].includes(key) && validId(value)) result[key] = value;
    else if (key === "detection_type" && /^[a-z0-9_-]{1,80}$/i.test(value)) result[key] = value.toLowerCase();
    else if (key === "status" && STATUSES.has(value)) result[key] = value;
    else if (key === "severity" && SEVERITIES.has(value.toUpperCase())) result[key] = value.toUpperCase();
    else if (["date_from", "date_to"].includes(key) && validDate(value)) result[key] = value;
    else if (key === "zone_name" && value.length <= 64 && !Array.from(value).some((char) => char.charCodeAt(0) < 32)) result[key] = value;
    else if (key === "critical_open" && value === "1") result[key] = value;
  }
  if (result.date_from && result.date_to && result.date_from > result.date_to) {
    result.date_from = "";
    result.date_to = "";
  }
  if (profile === "detectionAnalytics") {
    result.detection_type = "shoplifting";
    if (result.severity === "UNKNOWN") result.severity = "";
  }
  if (profile === "executive" || profile === "operations") result.days ||= "7";
  if ((profile === "detectionAnalytics" || profile === "transversalAnalytics") && !result.date_from && !result.date_to) {
    const duration = Number(result.days || 30);
    result.date_from = localDate(new Date(Date.now() - (duration - 1) * 86400000));
    result.date_to = localDate(new Date());
  }
  return result;
};

export const sharedQuery = (filters) => {
  const params = new URLSearchParams();
  KEYS.forEach((key) => { if (filters[key]) params.set(key, filters[key]); });
  return params.toString();
};

export const forEndpoint = (filters, endpoint) => {
  const params = {};
  (API_KEYS[endpoint] || []).forEach((key) => {
    if (!filters[key]) return;
    if (endpoint === "detectionAnalytics" && key === "severity" && filters[key] === "UNKNOWN") return;
    params[key] = filters[key];
  });
  if (endpoint === "executive" || endpoint === "operations") params.days = Number(filters.days || 7);
  return params;
};

const PATH_KEYS = {
  "/overview": API_KEYS.executive,
  "/monitoring/operations": API_KEYS.operations,
  "/monitoring/cameras": API_KEYS.operations,
  "/incidents": ["days", ...API_KEYS.alerts, "alert_id"],
  "/analytics/loss-prevention": ["days", ...API_KEYS.detectionAnalytics],
  "/analytics/detections": ["days", ...API_KEYS.transversalAnalytics],
};

export const buildSharedPath = (path, current, extra = {}, period = null) => {
  if (!PATH_KEYS[path]) return path;
  const source = parseSharedFilters(current);
  const merged = { ...source };
  if (period && (path === "/incidents" || path === "/analytics/loss-prevention" || path === "/analytics/detections")) {
    merged.date_from = period.starts_on || merged.date_from;
    merged.date_to = period.ends_on || merged.date_to;
  }
  Object.assign(merged, extra);
  if (extra.branch_id && String(extra.branch_id) !== source.branch_id) merged.camera_id = "";
  const allowed = Object.fromEntries(PATH_KEYS[path].map((key) => [key, merged[key] || ""]));
  const query = sharedQuery(parseSharedFilters(new URLSearchParams(allowed)));
  return query ? `${path}?${query}` : path;
};

export const useSharedFilters = (profile) => {
  const [params, setParams] = useSearchParams();
  const raw = params.toString();
  const canonical = sharedQuery(parseSharedFilters(new URLSearchParams(raw), profile));
  const filters = useMemo(() => parseSharedFilters(new URLSearchParams(canonical), profile), [canonical, profile]);

  useEffect(() => {
    if (raw !== canonical) setParams(new URLSearchParams(canonical), { replace: true });
  }, [raw, canonical, setParams]);

  const setFilter = (key, value) => {
    if (!KEYS.includes(key)) return;
    const next = parseSharedFilters(new URLSearchParams({ ...filters, [key]: String(value ?? "") }), profile);
    if (key !== "alert_id" && next[key] !== filters[key]) next.alert_id = "";
    if (key === "branch_id" && next.branch_id !== filters.branch_id) next.camera_id = "";
    const query = sharedQuery(next);
    if (query !== canonical) setParams(new URLSearchParams(query));
  };
  const clearFilters = () => {
    const retained = profile === "executive" || profile === "operations" ? { days: filters.days } : {};
    const next = parseSharedFilters(new URLSearchParams(retained), profile);
    const query = sharedQuery(next);
    if (query !== canonical) setParams(new URLSearchParams(query));
  };
  const pathFor = (path, extra = {}, period = null) => buildSharedPath(path, new URLSearchParams(canonical), extra, period);
  return { filters, setFilter, clearFilters, pathFor, endpointParams: (endpoint) => forEndpoint(filters, endpoint), query: canonical };
};
